import Axios, { AxiosError, AxiosRequestConfig } from 'axios';
import { translate } from 'shared/utils/translation';
import { baseApiUrl, PHONE_NUMBER, PIN } from '../consts';
import { ELang, EToken } from '../enum';
import { ErrorCode, IErrorValidation, ITranslatedError } from '../model';
import { refreshToken } from 'shared/context/WithGeneralInfo/refresh';

type TRequestMethod = 'get' | 'post' | 'put';

declare module 'axios' {
  export interface InternalAxiosRequestConfig {
    _retry?: boolean;
  }
}

interface AxiosResp<T> {
  headers?: AxiosRequestConfig['headers'];
  data: T;
  status?: number;
}
interface RestOptions {
  skipGlobalError?: boolean;
  withRespHeaders?: boolean;
  errorTitle?: string;
}

export const restClient = (lang: ELang, commonErrorCallback: () => void) => {
  const axios = Axios.create();

  const buildConfig = <D>(method: TRequestMethod, url: string, data?: D, config: AxiosRequestConfig = {}, lang?: ELang): AxiosRequestConfig => ({
    ...config,
    baseURL: baseApiUrl,
    headers: {
      ...config.headers,
      'Accept-Language': lang,
      Authorization: `Bearer ${localStorage.getItem(EToken.ACCESS)}`,
    },
    data,
    method,
    url,
  });

  const handleUnauthorized = async <T>(
    error: AxiosError,
    axiosInstance: typeof axios,
    commonErrorCallback: () => void
  ): Promise<AxiosResp<T> | null> => {
    const originalRequest = error.config;

    // Token expiration case
    const isTokenExpired = error.response?.status === 401;

    // If this is not a token expiration — don’t refresh.
    if (!isTokenExpired) {
      return null;
    }

    // If it has already been retried — avoid the loop.
    if (originalRequest._retry) {
      commonErrorCallback();
      return null;
    }

    originalRequest._retry = true;

    const newToken = await refreshToken();

    if (!newToken) {
      commonErrorCallback();
      return null;
    }

    localStorage.setItem(EToken.ACCESS, newToken);

    originalRequest.headers = {
      ...originalRequest.headers,
      Authorization: `Bearer ${newToken}`,
    };

    const retryResponse = await axiosInstance.request<T>(originalRequest);
    return { data: retryResponse.data };
  };

  const shouldSkipGlobalError = (error: AxiosError): boolean => {
    const status = error.response?.status;
    const errorMessages = (error.response?.data as any)?.errors;
    const errCode = (error.response?.data as any)?.error;

    if (status === 409 || errorMessages?.length) return true;
    if (status === 400 && (errCode === ErrorCode.DROPOFF_OUTSIDE_OF_DELIVERY_AREA || errCode === ErrorCode.VENUE_CLOSED)) return true;

    return false;
  };

  const requestApi = async <D, T>(
    method: TRequestMethod,
    url: string,
    data?: D,
    config: AxiosRequestConfig = {},
    options: RestOptions = {}
  ): Promise<AxiosResp<T>> => {
    const requestConfig = buildConfig(method, url, data, config, lang);

    try {
      const axiosResponse = await axios.request<T>(requestConfig);
      return {
        data: axiosResponse.data,
        ...(options.withRespHeaders ? { headers: axiosResponse.headers } : {}),
      };
    } catch (err) {
      const error = err as AxiosError;
      if (!Axios.isAxiosError(error)) {
        console.error('Unexpected error:', error);
        throw err;
      }

      const retryResult = await handleUnauthorized<T>(error, axios, commonErrorCallback);
      if (retryResult) return retryResult;

      const skipGlobalError = shouldSkipGlobalError(error);
      if (!options.skipGlobalError && !skipGlobalError) {
        commonErrorCallback();
      }

      throw err;
    }
  };

  const get = <T>(url: string, config?: AxiosRequestConfig, options?: RestOptions) =>
    requestApi<undefined, T>('get', url, undefined, config, options);

  const post = <D, T>(url: string, data?: D, config?: AxiosRequestConfig, options?: RestOptions) =>
    requestApi<D, T>('post', url, data, config, options);

  const put = <D, T>(url: string, data?: D, config?: AxiosRequestConfig, options?: RestOptions) =>
    requestApi<D, T>('put', url, data, config, options);

  return { get, post, put };
};


export const getErrorCodeTranslation = (errors: any) => {
const errorTexts: ITranslatedError[] = [];

  errors.forEach((err: IErrorValidation) => {
    switch (err.code) {
      case 'CARD_CHECK_RESIDENCE_FAILED':
        return errorTexts.push({message: translate('common.error.foreignCardTransferNotAvailable'), type: PIN});
      case 'CARD_CHECK_PAN_FAILED':
        return errorTexts.push({message: translate('common.error.cardNumberNotFound'), type: PIN});
      case 'PIN_NOT_FOUND':
        return errorTexts.push({message: translate('common.error.pinNotCorrect'), type: PIN});
      case 'PHONE_NUMBER_DOES_NOT_MATCHED':
      return errorTexts.push({message: err.message, type: PHONE_NUMBER});
      case 'GENERAL_PIN':
        return errorTexts.push({message: translate('common.error.finLength'), type: PIN});
      default:
        return null;
    }
  });

  return errorTexts;
};